import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PlaceOfResidence } from 'src/app/base/models/models.swagger.generated';
import { DateTime, Info } from 'luxon';
import { MultiFieldDurationPickerHelper } from 'src/app/base/shared/multi-field-duration-picker/multi-field-duration-picker-helper';
import { TariffGeneralFormType } from '../tariff-details-from-types';
import { MaskedDateTimeService, TimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { DaySelectorComponent } from '../../../../../../base/shared/day-selector/day-selector.component';

import { MultiFieldDurationPickerComponent } from '../../../../../../base/shared/multi-field-duration-picker/multi-field-duration-picker.component';
import { YesNoGroupButtonComponent } from '../../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { ButtonGroupComponent } from '../../../../../../base/shared/button-group/button-group.component';
import { AppErrorMessagesComponent } from '../../../../../../base/error-messages/app-error-messages.component';

export interface ITariffTimeWindow {
  startDay: string;
  startTime: string;
  endDay: string;
  endTime: string;
}

@Component({
    selector: 'app-tariff-details-general',
    templateUrl: './tariff-details-general.component.html',
    styleUrls: ['./tariff-details-general.component.scss'],
    imports: [ReactiveFormsModule, AppErrorMessagesComponent, ButtonGroupComponent, YesNoGroupButtonComponent, MultiFieldDurationPickerComponent, DaySelectorComponent, TimePickerModule, TranslateModule],
    providers: [MaskedDateTimeService]
})
export class TariffDetailsGeneralComponent implements OnInit, OnDestroy {

  @Input() generalFormGroup: FormGroup<TariffGeneralFormType>;

  public endTimeStr = null;

  public timeWindows: ITariffTimeWindow[] = [];

  public PlaceOfResidence = PlaceOfResidence;

  public isTwentyFourSevenTariff = false;

  public get showTimeWindowsError(): boolean {
    return this.generalFormGroup.controls.timeWindows.hasError('timeWindowOverlap');
  }

  private subscriptions = new Subscription();

  constructor(private translate: TranslateService) {
  }

  ngOnInit(): void {
    this.initTimeWindows();
    this.subscriptions.add(this.generalFormGroup.controls.timeWindows.valueChanges.subscribe(_ => this.initTimeWindows()));
    this.subscriptions.add(this.translate.onLangChange.subscribe(_ =>this.initTimeWindows()));
    this.subscriptions.add(this.generalFormGroup.controls.isTwentyFourSevenTariff.valueChanges.subscribe(_ => {
      this.updateControlStatesDependingOnTwentyFourSevenTariff();
      this.initTimeWindows();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateControlStatesDependingOnTwentyFourSevenTariff() {
    if (this.generalFormGroup?.disabled) {
      return;
    }
    const timeWindowsGroup = this.generalFormGroup.controls.timeWindows;
    if (this.generalFormGroup.controls.isTwentyFourSevenTariff.value) {
      timeWindowsGroup.disable();
    }
    else {
      timeWindowsGroup.enable();
    }
  }

  private initTimeWindows() {
    this.timeWindows = [];
    this.endTimeStr = null;
    this.isTwentyFourSevenTariff = false;
    if (this.generalFormGroup.controls.isTwentyFourSevenTariff?.value) {
      this.isTwentyFourSevenTariff = true;
      return;
    }
    const durationValues = this.generalFormGroup.controls.timeWindows.controls.durationForm.value;
    const duration = MultiFieldDurationPickerHelper.calculateTotalMinutes(durationValues?.days, durationValues?.hours, durationValues?.minutes, false);
    const startTime = this.generalFormGroup.controls.timeWindows.controls.startTime.value;
    const oneDayInMinutes = 24 * 60;
    if (startTime && duration) {
      const startTimeInMinutesSinceDayStart = MultiFieldDurationPickerHelper.calculateTotalMinutes(0, startTime.getHours(), startTime.getMinutes(), false);
      if (duration < oneDayInMinutes) {
        this.endTimeStr = DateTime.fromJSDate(startTime).plus({minutes: duration}).toFormat('HH:mm');
        if (startTimeInMinutesSinceDayStart + duration >= 24 * 60) {
          this.endTimeStr = '(+1) ' + this.endTimeStr;
        }
      } else {
        const startDateTime = DateTime.fromJSDate(startTime);
        const endDateTime = startDateTime.plus({minutes: duration});
        const daysShifted = Math.floor((startTimeInMinutesSinceDayStart + duration) / 60 / 24);
        const weekdays = this.generalFormGroup.controls.timeWindows.controls.weekdays.value;
        if (weekdays.allowedMonday) {
          this.addTimeWindow(0, startDateTime, endDateTime, daysShifted);
        }
        if (weekdays.allowedTuesday) {
          this.addTimeWindow(1, startDateTime, endDateTime, daysShifted);
        }
        if (weekdays.allowedWednesday) {
          this.addTimeWindow(2, startDateTime, endDateTime, daysShifted);
        }
        if (weekdays.allowedThursday) {
          this.addTimeWindow(3, startDateTime, endDateTime, daysShifted);
        }
        if (weekdays.allowedFriday) {
          this.addTimeWindow(4, startDateTime, endDateTime, daysShifted);
        }
        if (weekdays.allowedSaturday) {
          this.addTimeWindow(5, startDateTime, endDateTime, daysShifted);
        }
        if (weekdays.allowedSunday) {
          this.addTimeWindow(6, startDateTime, endDateTime, daysShifted);
        }
      }
    }
  }

  private addTimeWindow(day: number, startDateTime: DateTime, endDateTime: DateTime, daysShifted: number) {
    const window = {
      startDay: Info.weekdays('short')[day],
      startTime: startDateTime.toFormat('HH:mm'),
      endDay: Info.weekdays('short')[(day + daysShifted) % 7],
      endTime: endDateTime.toFormat('HH:mm')
    };
    this.timeWindows.push(window);
  }
}

